import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { RefreshCw, X } from 'react-feather';
import { toast } from 'react-toastify';
import {
  fetchTasksMe,
  isTasksLeaderRole,
  RM_TASKS_ENABLED,
  tasksApi,
} from '../../../Tasks/tasksApi';
import {
  Badge, Button, Card, EmptyState, Field, Loader, Page, PageHeader, SearchInput, Select, Table, TextInput, Toolbar,
  useConfirm,
} from '../../../ui';
import type { BadgeTone } from '../../../ui';
import { Callout, IconButton, Segmented } from './ui-pending';
import './RmPodsPage.scss';

/**
 * Consumer Xperience → RM Pods: which desk covers which countries, and who
 * is on each desk.
 *
 * Routing goes learner's timezone → country → pod → RM. The first hop is
 * IANA's own table and nobody here maintains it; the second is the business
 * decision, and this screen is where it is made.
 *
 * Two ways to read the same thing, because people arrive with both
 * questions: **By pod** ("who covers Brazil?") and **By RM** ("which desks
 * is Seerat on?"). An RM can be on more than one — India is served by three.
 */

/** What `POST /v1/pods/:key/rebalance` returns — the plan, before or after. */
interface RebalancePlan {
  podKey: string;
  podName: string;
  rmCount: number;
  totalLearners: number;
  target: number;
  givers: { externalId: string; name: string; has: number; release: number; moving: number; contacted: number }[];
  gainers: { externalId: string; name: string; has: number; gains: number }[];
  totalMoving: number;
  /** How far short of level it falls, because those learners have met their RM. */
  shortfall: number;
  applied: boolean;
}

interface PodAgent {
  externalId: string;
  name: string;
  role: string;
  isActive: boolean;
  phoneE164: string | null;
  availability: 'ON_SHIFT' | 'OFF_SHIFT' | 'ON_LEAVE';
  onLeaveUntil: string | null;
  workingDays: string[];
  /** Their existing caseload. Pod membership never changes it. */
  learners: number;
}

interface PodCountry {
  countryCode: string;
  name: string;
  learners: number;
  /** Other pods also covering it. */
  alsoIn: string[];
}

interface Pod {
  key: string;
  name: string;
  shiftLabel: string | null;
  isDefault: boolean;
  countries: PodCountry[];
  agents: PodAgent[];
  learners: number;
  /** Covers learners but has nobody on the desk. */
  stranded: boolean;
}

interface PodGap {
  countryCode: string;
  name: string;
  learners: number;
  suggestedPod: string | null;
  suggestedShift: string | null;
  localHours: string | null;
}

interface Overview {
  pods: Pod[];
  gaps: PodGap[];
  unresolvedLearners: number;
  totalLearners: number;
}

interface AgentRow {
  externalId: string;
  name: string;
  role: string;
  isActive: boolean;
}

/** One tone per known desk, so a pod reads the same in every view. Unknown pods fall back to primary. */
const POD_TONE: Record<string, BadgeTone> = {
  US_CANADA: 'info',
  IND_UAE: 'success',
  IND_UK: 'accent',
  IND_REST: 'warning',
};
const podTone = (key: string): BadgeTone => POD_TONE[key] ?? 'primary';

const AVAIL: Record<string, { label: string; tone: BadgeTone }> = {
  ON_SHIFT: { label: 'On shift', tone: 'success' },
  OFF_SHIFT: { label: 'Off shift', tone: 'neutral' },
  ON_LEAVE: { label: 'On leave', tone: 'danger' },
};

const num = (n: number): string => n.toLocaleString('en-IN');

const RmPodsPage: React.FC = () => {
  const [allowed, setAllowed] = useState<boolean | null>(null);
  const [isLeader, setIsLeader] = useState(false);
  const [data, setData] = useState<Overview | null>(null);
  const [agents, setAgents] = useState<AgentRow[]>([]);
  const [view, setView] = useState<'pods' | 'rms'>('pods');
  const [busy, setBusy] = useState(false);
  const [search, setSearch] = useState('');
  /** Pod key whose "add an RM" picker is open. */
  const [adding, setAdding] = useState<string>('');
  const [pickedAgent, setPickedAgent] = useState<string>('');
  const [addingCountryTo, setAddingCountryTo] = useState<string>('');
  const [countryCodes, setCountryCodes] = useState<string>('');
  const { confirm, dialog } = useConfirm();

  const load = useCallback(async () => {
    try {
      setData(await tasksApi<Overview>('/v1/pods'));
    } catch (err: any) {
      toast.error(err.message || 'Could not load pods');
      setData(null);
    }
  }, []);

  useEffect(() => {
    fetchTasksMe().then((me) => {
      setAllowed(!!me);
      const leader = isTasksLeaderRole(me?.role);
      setIsLeader(leader);
      if (leader) {
        tasksApi<AgentRow[]>('/v1/agents')
          .then((rows) => setAgents(rows.filter((a) => a.isActive)))
          .catch(() => setAgents([]));
      }
    });
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  /** The same memberships, turned inside out: one row per RM. */
  const byRm = useMemo(() => {
    const m = new Map<string, { agent: PodAgent; pods: Pod[] }>();
    for (const pod of data?.pods ?? []) {
      for (const a of pod.agents) {
        const row = m.get(a.externalId);
        if (row) row.pods.push(pod);
        else m.set(a.externalId, { agent: a, pods: [pod] });
      }
    }
    const q = search.trim().toLowerCase();
    // Array.from, not a spread: the build target predates Map iteration.
    return Array.from(m.values())
      .filter(({ agent }) => !q || agent.name.toLowerCase().includes(q) || agent.externalId.includes(q))
      .sort((x, y) => x.agent.name.localeCompare(y.agent.name));
  }, [data, search]);

  /** Provisioned RMs who are on no pod at all — they get handed nothing. */
  const unpodded = useMemo(() => {
    const on = new Set((data?.pods ?? []).flatMap((p) => p.agents.map((a) => a.externalId)));
    return agents.filter((a) => a.role === 'AGENT' && !on.has(a.externalId));
  }, [agents, data]);

  /**
   * Level the desk once an RM is on it.
   *
   * Shown before anything moves. A TL confirming this is handing over
   * hundreds of learner relationships, and the number belongs in front of
   * them rather than in a log afterwards. The alternative — adding the RM and
   * leaving the desk lopsided — is worse than it sounds: assignment picks
   * whoever holds the fewest learners, so a new RM with none absorbs every
   * onboarding until they catch up, which is the slowest work there is.
   */
  const offerRebalance = async (podKey: string, externalId: string, name: string) => {
    const plan = await tasksApi<RebalancePlan>(`/v1/pods/${podKey}/rebalance`, {
      method: 'POST',
      body: JSON.stringify({ toExternalId: externalId }),
    });
    if (!plan.totalMoving) {
      await confirm({
        title: `${name} is on ${plan.podName}`,
        message: 'No learners will move — this desk has nobody to level against yet.',
        acknowledge: true,
      });
      return;
    }
    const gives = plan.givers.filter((g) => g.moving > 0);
    const ok = await confirm({
      title: `Add ${name} to ${plan.podName}?`,
      message: (
        <>
          <p>
            {plan.podName} has {plan.rmCount} RMs holding {plan.totalLearners} learners. This levels
            it to <strong>{plan.target} each</strong>.
          </p>
          <ul className="rp-rebalance">
            {gives.map((g) => (
              <li key={g.externalId}>
                {g.name} — {g.moving}
                {g.moving < g.release && (
                  <span className="ds-text-subtle">
                    {' '}
                    of {g.release} · {g.contacted} have already spoken to them
                  </span>
                )}
              </li>
            ))}
          </ul>
          {plan.shortfall > 0 && (
            <p className="ds-text-subtle">
              {plan.shortfall} short of an even desk — those learners have already spoken to their
              RM, so they stay. You can move the rest by hand later.
            </p>
          )}
          <p className="ds-text-subtle">Learners who have already spoken to their RM stay where they are.</p>
        </>
      ),
      confirmLabel: `Add and move ${plan.totalMoving} learners`,
      cancelLabel: "Don't add them yet",
    });
    // Backing out undoes the whole thing, rather than leaving them on the
    // desk with no learners. There is no way to ask for the shuffle again
    // later, so "added but not levelled" would be a dead end reached by one
    // mis-click — and the RM would sit there absorbing every new onboarding
    // because assignment hands work to whoever holds the fewest learners.
    if (!ok) {
      await tasksApi(`/v1/pods/${podKey}/agents/${encodeURIComponent(externalId)}`, {
        method: 'DELETE',
      }).catch(() => undefined);
      toast.info(`${name} was not added. Nothing changed.`);
      return;
    }
    const done = await tasksApi<RebalancePlan & { moved: number }>(
      `/v1/pods/${podKey}/rebalance`,
      { method: 'POST', body: JSON.stringify({ toExternalId: externalId, apply: true }) },
    );
    toast.success(`Moved ${done.moved} learners to ${name}`);
  };

  const addAgent = async (podKey: string) => {
    if (!pickedAgent) return toast.warn('Pick an RM first');
    const picked = agents.find((a) => a.externalId === pickedAgent);
    setBusy(true);
    try {
      const r = await tasksApi<{ added: boolean }>(`/v1/pods/${podKey}/agents`, {
        method: 'POST',
        body: JSON.stringify({ agentExternalId: pickedAgent }),
      });
      setAdding('');
      setPickedAgent('');
      if (r.added) {
        await offerRebalance(podKey, pickedAgent, picked?.name ?? 'them');
      } else {
        toast.info('Already on that pod');
      }
      await load();
    } catch (err: any) {
      // One desk per RM. The refusal is three steps in order, which is more
      // than a toast can carry before it fades.
      if (err.status === 409) {
        await confirm({
          title: 'That RM is already on another desk',
          message: err.message,
          acknowledge: true,
        });
      } else {
        toast.error(err.message || 'Could not add them');
      }
    } finally {
      setBusy(false);
    }
  };

  const removeAgent = async (podKey: string, a: PodAgent) => {
    // Say the consequence before it happens — the mistake a TL makes on day
    // one is assuming this hands their learners to somebody else.
    const ok = await confirm({
      title: `Take ${a.name} off this pod?`,
      message:
        a.learners > 0
          ? `They keep their ${a.learners} existing learner${a.learners === 1 ? '' : 's'} — this only stops them being handed NEW ones from these countries. To move a learner, use Change RM on that learner's profile.`
          : undefined,
      confirmLabel: 'Remove',
      tone: 'danger',
    });
    if (!ok) return;
    setBusy(true);
    try {
      const r = await tasksApi<{ removed: boolean; keepsLearners: number }>(
        `/v1/pods/${podKey}/agents/${encodeURIComponent(a.externalId)}`,
        { method: 'DELETE' },
      );
      toast.success(
        r.keepsLearners > 0
          ? `Removed. ${a.name} still owns ${r.keepsLearners} learner${r.keepsLearners === 1 ? '' : 's'}.`
          : 'Removed',
      );
      await load();
    } catch (err: any) {
      toast.error(err.message || 'Could not remove them');
    } finally {
      setBusy(false);
    }
  };

  const addCountries = async (podKey: string) => {
    const codes = countryCodes.split(/[\s,]+/).map((c) => c.trim().toUpperCase()).filter(Boolean);
    if (!codes.length) return toast.warn('Enter one or more country codes');
    setBusy(true);
    try {
      const r = await tasksApi<{ added: number }>(`/v1/pods/${podKey}/countries`, {
        method: 'POST',
        body: JSON.stringify({ countryCodes: codes }),
      });
      toast.success(`${r.added} country${r.added === 1 ? '' : 'ies'} added`);
      setAddingCountryTo('');
      setCountryCodes('');
      await load();
    } catch (err: any) {
      toast.error(err.message || 'Could not add those countries');
    } finally {
      setBusy(false);
    }
  };

  const removeCountry = async (podKey: string, c: PodCountry) => {
    const extra = c.alsoIn.length
      ? `${c.name} is also covered by ${c.alsoIn.join(', ')}, so its learners stay routable.`
      : c.learners > 0
        ? `No other pod covers ${c.name}. Its ${num(c.learners)} learners will have no desk until one claims it.`
        : '';
    if (!(await confirm({
      title: `Remove ${c.name} from this pod?`,
      message: extra || undefined,
      confirmLabel: 'Remove',
      tone: 'danger',
    }))) return;
    setBusy(true);
    try {
      await tasksApi(`/v1/pods/${podKey}/countries/${c.countryCode}`, { method: 'DELETE' });
      toast.success(`${c.name} removed`);
      await load();
    } catch (err: any) {
      toast.error(err.message || 'Could not remove it');
    } finally {
      setBusy(false);
    }
  };

  const assignGap = async (gap: PodGap) => {
    if (!gap.suggestedPod) return;
    const pod = data?.pods.find((p) => p.key === gap.suggestedPod);
    if (!(await confirm({
      title: `Put ${gap.name} (${num(gap.learners)} learners) on ${pod?.name ?? gap.suggestedPod}?`,
      message: `That desk's ${gap.suggestedShift ?? 'shift'} reaches them ${gap.localHours ?? ''} their local time.`,
      confirmLabel: 'Accept',
    }))) return;
    setBusy(true);
    try {
      await tasksApi(`/v1/pods/${gap.suggestedPod}/countries`, {
        method: 'POST',
        body: JSON.stringify({ countryCodes: [gap.countryCode] }),
      });
      toast.success(`${gap.name} → ${pod?.name ?? gap.suggestedPod}`);
      await load();
    } catch (err: any) {
      toast.error(err.message || 'Could not assign it');
    } finally {
      setBusy(false);
    }
  };

  if (!RM_TASKS_ENABLED) {
    return (
      <Page>
        <EmptyState title="RM Pods isn't available" description="This feature isn't enabled in this environment yet." />
      </Page>
    );
  }
  if (allowed === null) {
    return <Page><Loader label="Checking access…" /></Page>;
  }
  if (!allowed) {
    return (
      <Page>
        <EmptyState
          title="Pods are for the RM team"
          description="You don't have an RM record in this tool."
        />
      </Page>
    );
  }

  const gapLearners = (data?.gaps ?? []).reduce((s, g) => s + g.learners, 0);

  return (
    <Page>
      {dialog}
      <PageHeader
        eyebrow="RM Tasks"
        title="RM Pods"
        subtitle={
          <>
            Which desk covers which countries, and who is on each desk. A learner&apos;s
            timezone gives their country; the country decides the pod.
          </>
        }
        actions={
          <div className="rm-row">
            <Segmented
              ariaLabel="View"
              size="sm"
              value={view}
              onChange={setView}
              options={[
                { value: 'pods', label: 'By pod' },
                { value: 'rms', label: 'By RM' },
              ]}
            />
            <IconButton aria-label="Reload" title="Reload" onClick={() => void load()}>
              <RefreshCw size={16} aria-hidden />
            </IconButton>
          </div>
        }
      />

      {data === null ? (
        <Loader label="Loading pods…" />
      ) : (
        <>
          {/* Holes first: a country nobody covers gets no RM at all. */}
          {data.gaps.length > 0 && (
            <Card
              flush
              title={
                <>
                  {data.gaps.length} countr{data.gaps.length === 1 ? 'y' : 'ies'} with{' '}
                  {num(gapLearners)} learner{gapLearners === 1 ? '' : 's'} belong to no pod
                </>
              }
              subtitle={
                <>
                  They route nowhere until a desk claims them. The suggestion is the desk whose
                  shift covers most of that country&apos;s 9am&ndash;9pm &mdash; a suggestion, not
                  a decision.
                </>
              }
              actions={<Badge tone="warning">Unrouted</Badge>}
            >
              <Table caption="Countries with no pod">
                <thead>
                  <tr>
                    <th>Country</th>
                    <th className="ds-num">Learners</th>
                    <th>Suggested desk</th>
                    <th>Reachable, their time</th>
                    {isLeader && <th aria-label="Actions" />}
                  </tr>
                </thead>
                <tbody>
                  {data.gaps.map((g) => (
                    <tr key={g.countryCode}>
                      <td>
                        <span className="ds-strong">{g.name}</span>{' '}
                        <span className="rm-caption">{g.countryCode}</span>
                      </td>
                      <td className="ds-num">{num(g.learners)}</td>
                      <td>
                        {g.suggestedPod ? (
                          <span className={`ds-strong rmp-tone--${podTone(g.suggestedPod)}`}>
                            {data.pods.find((p) => p.key === g.suggestedPod)?.name ?? g.suggestedPod}
                          </span>
                        ) : (
                          <span className="ds-text-muted">no desk reaches them</span>
                        )}
                        {g.suggestedShift && <div className="rm-caption">{g.suggestedShift}</div>}
                      </td>
                      <td className="rmp-tabular">{g.localHours ?? '—'}</td>
                      {isLeader && (
                        <td className="rmp-cell-end">
                          {g.suggestedPod && (
                            <Button size="sm" variant="secondary" disabled={busy} onClick={() => void assignGap(g)}>
                              Accept
                            </Button>
                          )}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card>
          )}

          {unpodded.length > 0 && (
            <Callout tone="neutral" title={`${unpodded.length} RM${unpodded.length === 1 ? '' : 's'} on no pod`}>
              {unpodded.map((a) => a.name).join(', ')}. They keep any learners they
              already own but will not be handed new ones.
            </Callout>
          )}

          {view === 'pods' ? (
            <div className="rmp-grid">
              {data.pods.map((pod) => (
                <Card key={pod.key} className="rmp-pod">
                  <div className="rmp-pod__head">
                    <span className={`rmp-stripe rmp-stripe--${podTone(pod.key)}`} aria-hidden />
                    <div className="rmp-pod__heading">
                      <div className="rmp-pod__name">{pod.name}</div>
                      <div className="rm-caption">
                        {pod.shiftLabel ?? 'no shift recorded'} ·{' '}
                        {pod.agents.length} RM{pod.agents.length === 1 ? '' : 's'} ·{' '}
                        {num(pod.agents.reduce((n, a) => n + a.learners, 0))} on their lists
                      </div>
                      {/* Two different numbers, and they are meant to differ.
                          "Covers" counts everyone living in the countries this
                          desk claims — India is claimed by three desks on
                          purpose, so those add up to more than the team has.
                          "On their lists" is what these RMs actually hold. */}
                      <div className="rm-caption">
                        covers {num(pod.learners)} learner
                        {pod.learners === 1 ? '' : 's'} in its countries
                      </div>
                    </div>
                  </div>

                  {pod.stranded && (
                    <Callout tone="danger">
                      Nobody is on this desk — the {num(pod.learners)} learners in its
                      countries can&apos;t be handed to anyone.
                    </Callout>
                  )}

                  {/* RMs */}
                  <div>
                    <h3 className="rmp-section-label">RMs</h3>
                    {pod.agents.length === 0 ? (
                      <div className="ds-text-muted rmp-small">Nobody yet.</div>
                    ) : (
                      pod.agents.map((a) => {
                        const av = AVAIL[a.availability] ?? AVAIL.OFF_SHIFT;
                        return (
                          <div key={a.externalId} className="rmp-agent">
                            <div className="rmp-agent__who">
                              <span className="ds-strong">{a.name}</span>
                              {!a.isActive && <span className="rm-caption"> · inactive</span>}
                              <div className="rm-caption">
                                {num(a.learners)} learner{a.learners === 1 ? '' : 's'}
                                {a.workingDays.length ? ` · off ${
                                  ['MON','TUE','WED','THU','FRI','SAT','SUN']
                                    .filter((d) => !a.workingDays.includes(d)).join(', ') || 'never'
                                }` : ''}
                              </div>
                            </div>
                            <div className="rm-row">
                              <Badge tone={av.tone}>{av.label}</Badge>
                              {isLeader && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="rmp-remove"
                                  disabled={busy}
                                  onClick={() => void removeAgent(pod.key, a)}
                                >
                                  Remove
                                </Button>
                              )}
                            </div>
                          </div>
                        );
                      })
                    )}
                    {isLeader && (
                      adding === pod.key ? (
                        <div className="rmp-inline-form">
                          <Field label="RM to add" className="rmp-inline-form__grow">
                            {(f) => (
                              <Select
                                {...f}
                                size="sm"
                                value={pickedAgent}
                                onChange={(e) => setPickedAgent(e.target.value)}
                                options={[
                                  { value: '', label: 'Choose an RM…' },
                                  ...agents
                                    .filter((a) => !pod.agents.some((x) => x.externalId === a.externalId))
                                    .map((a) => ({
                                      value: a.externalId,
                                      label: `${a.name}${a.role !== 'AGENT' ? ` (${a.role.replace('_', ' ')})` : ''}`,
                                    })),
                                ]}
                              />
                            )}
                          </Field>
                          <Button size="sm" variant="primary" disabled={busy || !pickedAgent}
                            onClick={() => void addAgent(pod.key)}>
                            Add
                          </Button>
                          <Button size="sm" variant="secondary" onClick={() => { setAdding(''); setPickedAgent(''); }}>
                            Cancel
                          </Button>
                        </div>
                      ) : (
                        <Button size="sm" variant="secondary" className="rmp-add" onClick={() => setAdding(pod.key)}>
                          Add an RM
                        </Button>
                      )
                    )}
                  </div>

                  {/* Countries */}
                  <div>
                    <h3 className="rmp-section-label">Countries ({pod.countries.length})</h3>
                    <div className="rmp-countries">
                      {pod.countries.map((c) => (
                        <div
                          key={c.countryCode}
                          className="rmp-country"
                          title={c.alsoIn.length ? `Also covered by ${c.alsoIn.join(', ')}` : undefined}
                        >
                          <span className="rmp-country__name">
                            {c.name}
                            {c.alsoIn.length > 0 && (
                              <>
                                {' '}
                                <Badge tone="primary">
                                  +{c.alsoIn.length} pod{c.alsoIn.length === 1 ? '' : 's'}
                                </Badge>
                              </>
                            )}
                          </span>
                          <span className="rm-row">
                            <span className="rm-caption rmp-tabular">{num(c.learners)}</span>
                            {isLeader && (
                              <IconButton
                                className="rmp-remove"
                                aria-label={`Remove ${c.name}`}
                                title={`Remove ${c.name}`}
                                disabled={busy}
                                onClick={() => void removeCountry(pod.key, c)}
                              >
                                <X size={14} aria-hidden />
                              </IconButton>
                            )}
                          </span>
                        </div>
                      ))}
                    </div>
                    {isLeader && (
                      addingCountryTo === pod.key ? (
                        <div className="rmp-inline-form">
                          <Field label="Country codes" className="rmp-inline-form__grow">
                            {(f) => (
                              <TextInput {...f} size="sm" value={countryCodes} placeholder="BR, AR, CL"
                                onChange={(e) => setCountryCodes(e.target.value)} />
                            )}
                          </Field>
                          <Button size="sm" variant="primary" disabled={busy}
                            onClick={() => void addCountries(pod.key)}>
                            Add
                          </Button>
                          <Button size="sm" variant="secondary" onClick={() => { setAddingCountryTo(''); setCountryCodes(''); }}>
                            Cancel
                          </Button>
                        </div>
                      ) : (
                        <Button size="sm" variant="secondary" className="rmp-add" onClick={() => setAddingCountryTo(pod.key)}>
                          Add countries
                        </Button>
                      )
                    )}
                  </div>
                </Card>
              ))}
            </div>
          ) : (
            /* By RM — the inside-out view. One row per RM, pods they are on. */
            <Card flush>
              <Toolbar className="rmp-toolbar">
                <SearchInput
                  size="sm"
                  aria-label="Search RM by name or id"
                  placeholder="Search RM by name or id…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </Toolbar>
              {byRm.length === 0 ? (
                <EmptyState
                  title="No RMs on any pod yet"
                  description={search.trim() ? 'Try a different name or id.' : 'Add an RM to a pod from the By pod view.'}
                />
              ) : (
                <Table caption="RMs and their pods">
                  <thead>
                    <tr>
                      <th>RM</th>
                      <th>Pods</th>
                      <th>Shift</th>
                      <th>Week off</th>
                      <th>Availability</th>
                      <th className="ds-num">Learners</th>
                    </tr>
                  </thead>
                  <tbody>
                    {byRm.map(({ agent, pods }) => {
                      const av = AVAIL[agent.availability] ?? AVAIL.OFF_SHIFT;
                      const off = ['MON','TUE','WED','THU','FRI','SAT','SUN']
                        .filter((d) => !agent.workingDays.includes(d));
                      return (
                        <tr key={agent.externalId}>
                          <td>
                            <div className="ds-strong">{agent.name}</div>
                            <div className="rm-caption">
                              UserId · {agent.externalId}
                              {agent.phoneE164 ? ` · ${agent.phoneE164}` : ''}
                            </div>
                          </td>
                          <td>
                            <div className="rm-row rmp-chips">
                              {pods.map((p) => (
                                <Badge key={p.key} tone={podTone(p.key)}>{p.name}</Badge>
                              ))}
                            </div>
                          </td>
                          <td className="ds-text-muted">
                            {Array.from(new Set(pods.map((p) => p.shiftLabel).filter(Boolean))).join(' / ') || '—'}
                          </td>
                          <td className="ds-text-muted">{off.join(', ') || 'none'}</td>
                          <td><Badge tone={av.tone}>{av.label}</Badge></td>
                          <td className="ds-num ds-strong">{num(agent.learners)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              )}
            </Card>
          )}

          <p className="rm-caption rmp-footnote">
            {num(data.totalLearners)} active learners resolved by timezone. A desk&apos;s
            &ldquo;covers&rdquo; count is learners in the countries it claims, and India is
            claimed by three desks on purpose — so those figures overlap and will not
            sum to the total. &ldquo;On their lists&rdquo; is the real caseload and does.
            {data.unresolvedLearners > 0 && (
              <>
                {' '}
                {num(data.unresolvedLearners)} sit on a UTC-offset timezone
                (<span className="rm-mono">Etc/GMT…</span>) that names no country, so no
                pod can claim them by country.
              </>
            )}
            {' '}Pods do not yet drive assignment — routing still uses the old region field
            until this map is signed off.
          </p>
        </>
      )}
    </Page>
  );
};

export default RmPodsPage;
